import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const EASE = 'cubic-bezier(0.23,1,0.32,1)';

const INPUT_CLS =
  'w-full px-4 py-3 bg-cream-light border border-wood/15 rounded-sm font-sans text-sm text-wood placeholder:text-taupe/40 focus:outline-none focus:border-terracotta transition-colors duration-200';

const WA_ICON =
  'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z';

/* ─── Hero ─── */
function Hero() {
  const ref = useRef(null);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = ref.current;
    if (!el) return;
    const tl = gsap.timeline();
    tl.fromTo(el.querySelector('h1'), { opacity: 0, y: 28 }, { opacity: 1, y: 0, duration: 0.8, ease: EASE })
      .fromTo(el.querySelector('p'),  { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.65, ease: EASE }, '-=0.4');
    return () => tl.kill();
  }, []);

  return (
    <section
      ref={ref}
      className="flex items-center bg-cream-light overflow-hidden border-b border-gold/10"
      style={{ minHeight: '36vh' }}
      aria-label="Solicitar cotización"
    >
      <div className="container-custom py-16">
        <div className="max-w-xl">
          <span className="font-sans text-xs tracking-[0.3em] uppercase text-terracotta mb-5 block">
            Sin compromiso · Respuesta en 24h
          </span>
          <h1 className="font-serif text-display-xs md:text-display-sm text-wood mb-4 leading-tight">
            Solicitar Cotización
          </h1>
          <div className="w-14 h-px bg-gold mb-5" />
          <p className="font-sans text-base text-taupe leading-relaxed">
            Cotización gratuita · Sin compromiso · Respuesta en menos de 24 horas
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─── Quote Form ─── */
const INITIAL_FORM = {
  nombre:      '',
  email:       '',
  telefono:    '',
  ciudad:      '',
  pais:        'México',
  tipo:        '',
  madera:      '',
  medidas:     '',
  descripcion: '',
  referencia:  '',
};

function QuoteForm() {
  const ref = useRef(null);
  const [form, setForm] = useState(INITIAL_FORM);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = ref.current;
    if (!el) return;
    const targets = el.querySelectorAll('[data-reveal]');
    targets.forEach((t, i) => {
      gsap.fromTo(t, { opacity: 0, y: 22 }, {
        opacity: 1, y: 0, duration: 0.65, delay: i * 0.07, ease: EASE,
        scrollTrigger: { trigger: t, start: 'top 90%', toggleActions: 'play none none none' },
      });
    });
    return () => ScrollTrigger.getAll().forEach((st) => st.kill());
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    const lines = [
      `Hola Del Sol Muebles, me llamo ${form.nombre} y quisiera una cotización.`,
      ``,
      `📋 *Detalles del proyecto:*`,
      `• Tipo de mueble: ${form.tipo || 'No especificado'}`,
      `• Madera preferida: ${form.madera || 'Sin preferencia'}`,
      `• Medidas aproximadas: ${form.medidas || 'Sin especificar'}`,
      ``,
      `📝 *Descripción:*`,
      form.descripcion,
      ``,
      form.referencia ? `🖼️ Referencia: ${form.referencia}\n` : '',
      `📞 *Contacto:*`,
      `• Email: ${form.email}`,
      `• Teléfono: ${form.telefono || 'No proporcionado'}`,
      `• Ciudad: ${form.ciudad}`,
      `• País: ${form.pais}`,
    ].filter(Boolean).join('\n');

    window.open(`https://wa.me/523338283729?text=${encodeURIComponent(lines)}`, '_blank');
  };

  return (
    <section ref={ref} className="section-padding bg-cream">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">

          {/* ── Form — 2 cols ── */}
          <div className="lg:col-span-2">
            <div className="mb-10" data-reveal>
              <span className="font-sans text-xs tracking-[0.25em] uppercase text-terracotta/80 mb-3 block">
                Tu Información
              </span>
              <h2 className="font-serif text-display-xs text-wood">
                Cuéntanos qué necesitas
              </h2>
              <div className="w-10 h-px bg-gold mt-5" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" noValidate>

              {/* Block 1: Personal */}
              <fieldset className="space-y-5" data-reveal>
                <legend className="font-sans text-[10px] tracking-[0.25em] uppercase text-taupe/70 mb-3 pb-2 border-b border-wood/10 w-full">
                  Datos personales
                </legend>

                <div>
                  <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-taupe mb-2">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    name="nombre"
                    value={form.nombre}
                    onChange={handleChange}
                    required
                    placeholder="Tu nombre"
                    className={INPUT_CLS}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-taupe mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="tu@email.com"
                      className={INPUT_CLS}
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-taupe mb-2">
                      Teléfono / WhatsApp *
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      value={form.telefono}
                      onChange={handleChange}
                      required
                      placeholder="+52 33 XXXX XXXX"
                      className={INPUT_CLS}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-taupe mb-2">
                      Ciudad / Estado *
                    </label>
                    <input
                      type="text"
                      name="ciudad"
                      value={form.ciudad}
                      onChange={handleChange}
                      required
                      placeholder="Guadalajara, CDMX…"
                      className={INPUT_CLS}
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-taupe mb-2">
                      País
                    </label>
                    <select
                      name="pais"
                      value={form.pais}
                      onChange={handleChange}
                      className={INPUT_CLS}
                    >
                      <option value="México">México</option>
                      <option value="USA">Estados Unidos</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>
                </div>
              </fieldset>

              {/* Block 2: Product */}
              <fieldset className="space-y-5" data-reveal>
                <legend className="font-sans text-[10px] tracking-[0.25em] uppercase text-taupe/70 mb-3 pb-2 border-b border-wood/10 w-full">
                  Detalles del mueble
                </legend>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-taupe mb-2">
                      Tipo de Mueble *
                    </label>
                    <select
                      name="tipo"
                      value={form.tipo}
                      onChange={handleChange}
                      required
                      className={INPUT_CLS}
                    >
                      <option value="">Seleccionar…</option>
                      <option value="Mesa de Comedor">Mesa de Comedor</option>
                      <option value="Silla / Banco">Silla / Banco</option>
                      <option value="Mueble de Recámara">Mueble de Recámara</option>
                      <option value="Mueble Exterior / Terraza">Mueble Exterior / Terraza</option>
                      <option value="Proyecto Comercial (restaurante, bar, hotel)">Proyecto Comercial</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-taupe mb-2">
                      Madera Preferida
                    </label>
                    <select
                      name="madera"
                      value={form.madera}
                      onChange={handleChange}
                      className={INPUT_CLS}
                    >
                      <option value="">No tengo preferencia</option>
                      <option value="Parota">Parota (madera mexicana premium)</option>
                      <option value="Rosa Morada">Rosa Morada</option>
                      <option value="Tzalam">Tzalam</option>
                      <option value="Mezquite">Mezquite</option>
                      <option value="Teca">Teca (resistente al exterior)</option>
                      <option value="No tengo preferencia">Sin preferencia — recomendar</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-taupe mb-2">
                    Medidas Aproximadas
                  </label>
                  <input
                    type="text"
                    name="medidas"
                    value={form.medidas}
                    onChange={handleChange}
                    placeholder="Ej: 2m × 90cm, o no sé aún"
                    className={INPUT_CLS}
                  />
                </div>

                <div>
                  <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-taupe mb-2">
                    Descripción del Proyecto *
                  </label>
                  <textarea
                    name="descripcion"
                    value={form.descripcion}
                    onChange={handleChange}
                    required
                    rows={4}
                    placeholder="¿Cuántas piezas? ¿Qué acabado? ¿Para hogar o negocio? Cualquier detalle nos ayuda."
                    className={`${INPUT_CLS} resize-none`}
                  />
                </div>

                <div>
                  <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-taupe mb-2">
                    Referencia de Imagen (opcional)
                  </label>
                  <input
                    type="text"
                    name="referencia"
                    value={form.referencia}
                    onChange={handleChange}
                    placeholder="Link de foto o descripción de referencia"
                    className={INPUT_CLS}
                  />
                </div>
              </fieldset>

              {/* Submit */}
              <div data-reveal>
                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-terracotta text-white font-sans text-sm font-medium tracking-widest uppercase rounded-sm hover:bg-terracotta-dark transition-colors duration-300 shadow-sm"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d={WA_ICON} />
                  </svg>
                  Enviar Cotización por WhatsApp
                </button>
                <p className="font-sans text-[10px] text-taupe/50 text-center mt-3 tracking-wider">
                  Serás redirigido a WhatsApp con tu solicitud pre-redactada
                </p>
              </div>
            </form>
          </div>

          {/* ── Sidebar ── */}
          <div className="lg:col-span-1 space-y-6">

            {/* Process Steps */}
            <div className="bg-cream-light rounded-sm p-7 border border-gold/10" data-reveal>
              <h3 className="font-serif text-xl text-wood mb-6">Nuestro Proceso</h3>
              <div className="space-y-5">
                {[
                  { n: '01', title: 'Cotiza',      body: 'Envías tu solicitud — respondemos en 24h con precio y tiempos.' },
                  { n: '02', title: 'Diseñamos',   body: 'Refinamos medidas, madera y acabado según tu espacio.' },
                  { n: '03', title: 'Fabricamos',  body: 'Producción artesanal en nuestro taller en Tonalá.' },
                  { n: '04', title: 'Entregamos',  body: 'Embalaje especializado y envío a todo México.' },
                ].map((s) => (
                  <div key={s.n} className="flex items-start gap-4">
                    <span className="font-serif text-2xl text-gold/50 leading-none w-8 flex-shrink-0">{s.n}</span>
                    <div>
                      <p className="font-serif text-base text-wood">{s.title}</p>
                      <p className="font-sans text-xs text-taupe leading-relaxed mt-0.5">{s.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Direct contact */}
            <div className="bg-wood rounded-sm p-7" data-reveal>
              <h3 className="font-serif text-lg text-cream mb-4">
                ¿Prefieres hablar directamente?
              </h3>
              <div className="space-y-3">
                <a
                  href="tel:+523338283729"
                  className="flex items-center gap-3 w-full px-5 py-3 bg-cream/10 text-cream font-sans text-sm rounded-sm hover:bg-cream/20 transition-colors duration-200"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                  </svg>
                  +52 33 3828 3729
                </a>
                <a
                  href="https://wa.me/523338283729"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 w-full px-5 py-3 bg-[#25D366] text-white font-sans text-sm rounded-sm hover:bg-[#1ebe5d] transition-colors duration-200"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d={WA_ICON} />
                  </svg>
                  WhatsApp directo
                </a>
              </div>
            </div>

            {/* See catalog */}
            <div className="rounded-sm p-7 border border-gold/20 text-center" data-reveal>
              <p className="font-sans text-sm text-taupe mb-4 leading-relaxed">
                ¿Quieres explorar más opciones antes de cotizar?
              </p>
              <Link to="/coleccion" className="btn-outline w-full text-center block">
                Ver Catálogo
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Page ─── */
export default function Cotizacion() {
  return (
    <main>
      <Hero />
      <QuoteForm />
    </main>
  );
}
